import { take, put, all } from 'redux-saga/effects'
import { keys, isEmpty } from 'ramda'
import { deleteSystemNotification } from 'actions/application'
import {
  RESET_FEATURE,
  FEATURE_SAVED,
  RESET_PRE_CREATE_NEW_FEATURE,
  OBSOLETE_FEATURE_SAVED,
} from 'actions/feature/features'
import { EXIT_SIMULATE_MODE } from 'actions/feature/simulation'
import {
  CLEAR_POWER_TAKE_OFF,
  CLEAR_SAVE_POWER_TAKE_OFF_ERROR,
} from 'actions/powerTakeOff'
import { NAVIGATOR_RESET } from 'actions/navigator'
import {
  DUPLICATE_REFERENCE_PARAM_FAIL,
  CANCEL_REFERENCE_PARAM_EDIT,
  SAVE_REFERENCE_PARAM,
  RESET_RP_STATE,
} from 'actions/referenceParameter'
import { CLEAR_ECP_STATE, ECP_ADD_WARNING_NOTIFY_CLEANUP } from 'actions/ecp'
import { VALIDATE_PAR_FILE_SUCCESS, GET_TRUCK_DETAILS } from 'actions/truck'
import {
  PTO_PENDING_UPDATE,
  SIMULATION_NOTIFY,
  EDIT_RP_NOTIFY,
  EDIT_FEATURE_NOTIFY,
  EDIT_SP_NOTIFY,
  DUPLICATE_MASTER_RP_NOTIFY,
  UPDATE_FLEET_FAIL_NOTIFY,
  PTO_SAVE_FAIL_NOTIFY,
  ECP_PENDING_UPDATE,
  ECP_PENDING_MULTIPLE_UPDATES,
  OPTIONAL_SOLENOIDS_NOT_INSTALLED,
  SNG_PENDING_UPDATE,
  SNG_SAVE_SUCCESS,
  PAR_FILE_DOWNLOAD_NOTIFY,
  ECP_ADD_WARNING_NOTIFY,
} from 'utils/notifications'
import { CANCEL_SUPPORT_PARAM_EDIT } from 'actions/supportParameter'
import { GET_FLEET_INFORMATION } from 'actions/fleet'
import {
  REMOVE_PARTS_WITHOUT_LOCATION_BANNER,
  REMOVE_SNG_PENDING_NOTIFICATION,
  SAVE_DASH_CONFIG,
  REQUESTING_DASH_CONFIG,
} from 'actions/dash'
import { ADD_COMPONENTS_WITHOUT_LOCATION_BANNER } from 'utils/constants'

const watcherDictionary = {
  [EXIT_SIMULATE_MODE]: handleExitSimulateMode,
  [CLEAR_POWER_TAKE_OFF]: removePendingPtoUpdates,
  [CLEAR_SAVE_POWER_TAKE_OFF_ERROR]: removePtoSaveError,
  [CLEAR_ECP_STATE]: removePendingEcpUpdates,
  [DUPLICATE_REFERENCE_PARAM_FAIL]: removeRPEditBanner,
  [CANCEL_REFERENCE_PARAM_EDIT]: removeRPEditBanner,
  [SAVE_REFERENCE_PARAM]: removeRPEditBanner,
  [RESET_RP_STATE]: removeRPEditBanner,
  [RESET_RP_STATE]: removeRPMaster32AP1Duplicate,
  [ECP_ADD_WARNING_NOTIFY_CLEANUP]: removeAddECPWarning,
  [CANCEL_SUPPORT_PARAM_EDIT]: removeSpEditBanner,
  [NAVIGATOR_RESET]: removeFeatureEditBanner,
  [RESET_FEATURE]: removeFeatureEditBanner,
  [RESET_PRE_CREATE_NEW_FEATURE]: removeFeatureEditBanner,
  [FEATURE_SAVED]: removeFeatureEditBanner,
  [OBSOLETE_FEATURE_SAVED]: removeObsoleteFeatureStatusBanner,
  [GET_FLEET_INFORMATION]: removeFleetUpdateFailBanner,
  [REMOVE_PARTS_WITHOUT_LOCATION_BANNER]: removePartsWithoutLocationBanner,
  [REMOVE_SNG_PENDING_NOTIFICATION]: removePendingSwitchAndGaugeNotification,
  [SAVE_DASH_CONFIG]: removeSaveRelatedNotifications,
  [REQUESTING_DASH_CONFIG]: removePendingSwitchAndGaugeNotification,
  [VALIDATE_PAR_FILE_SUCCESS]: removeDownloadSuccessNotification,
  [GET_TRUCK_DETAILS]: clearVehicleScopedNotifications,
}

const getNotificationInfoFromAction = (action) =>
  watcherDictionary[action.type](action)

export default function* watchForNotificationCleanup() {
  while (true) {
    const action = yield take(keys(watcherDictionary))
    const notificationInfo = getNotificationInfoFromAction(action)
    // Handle array of notification IDs (for clearing multiple notifications)
    if (Array.isArray(notificationInfo)) {
      yield all(
        notificationInfo
          .filter((info) => !isEmpty(info))
          .map((info) => put(deleteSystemNotification(info)))
      )
    } else if (!isEmpty(notificationInfo)) {
      yield put(deleteSystemNotification(notificationInfo))
    }
  }
}

function handleExitSimulateMode() {
  return { notificationId: SIMULATION_NOTIFY }
}

function removePendingPtoUpdates() {
  return { notificationId: PTO_PENDING_UPDATE }
}

function removePendingEcpUpdates() {
  // Remove all ECP-related pending notifications
  // API's pending flag is the single source of truth
  return [
    { notificationId: ECP_PENDING_UPDATE },
    { notificationId: ECP_PENDING_MULTIPLE_UPDATES },
    { notificationId: OPTIONAL_SOLENOIDS_NOT_INSTALLED },
  ]
}

function removeAddECPWarning() {
  return {
    notificationId: ECP_ADD_WARNING_NOTIFY,
  }
}

function removePtoSaveError() {
  return { notificationId: PTO_SAVE_FAIL_NOTIFY }
}

function removeRPEditBanner() {
  return { notificationId: EDIT_RP_NOTIFY }
}

function removeRPMaster32AP1Duplicate() {
  return { notificationId: DUPLICATE_MASTER_RP_NOTIFY }
}

function removeFeatureEditBanner() {
  return { notificationId: EDIT_FEATURE_NOTIFY }
}

function removeObsoleteFeatureStatusBanner() {
  return { notificationId: OBSOLETE_FEATURE_SAVED }
}

function removeSpEditBanner() {
  return { notificationId: EDIT_SP_NOTIFY }
}

function removeFleetUpdateFailBanner(action) {
  const { vehicles } = action
  const allVehiclesServiced = vehicles.every(({ serviceDate }) => !!serviceDate)
  if (allVehiclesServiced) {
    return { notificationId: UPDATE_FLEET_FAIL_NOTIFY }
  }
  return {}
}

function removePartsWithoutLocationBanner() {
  return { notificationId: ADD_COMPONENTS_WITHOUT_LOCATION_BANNER }
}

function removePendingSwitchAndGaugeNotification() {
  // API's pendingEdexUpdate flag is the single source of truth
  return { notificationId: SNG_PENDING_UPDATE }
}

function removeSaveRelatedNotifications() {
  return [
    { notificationId: SNG_PENDING_UPDATE },
    { notificationId: SNG_SAVE_SUCCESS },
  ]
}

function removeDownloadSuccessNotification() {
  return { notificationId: PAR_FILE_DOWNLOAD_NOTIFY }
}

/**
 * Clear all vehicle-scoped notifications when switching vehicles.
 * This fires on GET_TRUCK_DETAILS which occurs when Vehicle.jsx mounts
 * or when the URL serial number changes.
 * The notification saga will recreate them if the new vehicle has pending changes.
 */
export function clearVehicleScopedNotifications() {
  // Clear all pending notifications when switching vehicles
  // The notification saga will recreate them based on the new vehicle's API data
  // (pendingEdexUpdate flag is the single source of truth)
  return [
    { notificationId: SNG_PENDING_UPDATE },
    { notificationId: SNG_SAVE_SUCCESS },
    { notificationId: ECP_PENDING_UPDATE },
    { notificationId: ECP_PENDING_MULTIPLE_UPDATES },
    { notificationId: OPTIONAL_SOLENOIDS_NOT_INSTALLED },
  ]
}
